import { BackupService } from '@qdrant/qdrant-cloud-public-api/serverless/space/backup/v1';
import {
  listBackups,
  getBackup,
  createBackup,
  deleteBackup,
  restoreBackup,
  listBackupRestores,
  listBackupSchedules,
  getBackupSchedule,
  createBackupSchedule,
  updateBackupSchedule,
  deleteBackupSchedule,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/backup/v1/connect-query';
import { transport } from '@/api/transport';
import { createConnectQueryKey, createServiceQueries, type MutationFactory, queryOptions } from '@/connect-query';
import { useMutation } from '@/connect-query/hooks/useMutation';

export const spaceBackupService = createServiceQueries(BackupService, {
  getBackupQuery: (params) => queryOptions(getBackup, params, { transport }),
  getBackupScheduleQuery: (params) => queryOptions(getBackupSchedule, params, { transport }),
  listBackupRestoresQuery: (params) => queryOptions(listBackupRestores, params, { transport }),
  listBackupSchedulesQuery: (params) => queryOptions(listBackupSchedules, params, { transport }),
  listBackupsQuery: (params) => queryOptions(listBackups, params, { transport }),
});

// gRPC Mutations using MutationFactory pattern
export const useCreateSpaceBackupMutation: MutationFactory<typeof createBackup> = () =>
  useMutation(createBackup, {
    onSuccess: (_data, { backup }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId: backup?.accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useDeleteSpaceBackupMutation: MutationFactory<typeof deleteBackup> = () =>
  useMutation(deleteBackup, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupRestores,
            input: { accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useRestoreSpaceBackupMutation: MutationFactory<typeof restoreBackup> = () =>
  useMutation(restoreBackup, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupRestores,
            input: { accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useCreateSpaceBackupScheduleMutation: MutationFactory<typeof createBackupSchedule> = () =>
  useMutation(createBackupSchedule, {
    onSuccess: (_data, variables, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupSchedules,
            input: { accountId: variables.backupSchedule?.accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useUpdateSpaceBackupScheduleMutation: MutationFactory<typeof updateBackupSchedule> = () =>
  useMutation(updateBackupSchedule, {
    onSuccess: (_data, variables, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupSchedules,
            input: { accountId: variables.backupSchedule?.accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useDeleteSpaceBackupScheduleMutation: MutationFactory<typeof deleteBackupSchedule> = () =>
  useMutation(deleteBackupSchedule, {
    onSuccess: (_data, { accountId, deleteBackups }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupSchedules,
            input: { accountId },
          }),
        }),
        ...(deleteBackups
          ? [
              client.invalidateQueries({
                queryKey: createConnectQueryKey({
                  schema: listBackups,
                  input: { accountId },
                }),
              }),
            ]
          : []),
        router.invalidate(),
      ]),
  });
